import React, { useMemo, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const norm = (s) =>
    (s ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();

const DmeLogDetail = ({ data, onBack, hideCloseButton = false }) => {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        setPage(1);
    }, [q, data?.returnId]);

    const rows = data?.rows ?? [];

    const exportToExcel = () => {
        try {
            setExporting(true);
            const source = filtered; // usa pageRows si prefieres exportar solo la p�gina visible

            const dataForExcel = source.map((r) => ({
                Referencia: r.reference ?? "",
                Sucursal: r.branch ?? "",
                Cliente_Interno: r.clientQA ?? "",
                Nombre: r.clientName ?? "",
                SKU: r.sku ?? "",
                SKU_Interno: r.skuQA ?? "",
                Descripcion: r.skuDescription ?? "",
                MU: r.mu ?? r.MU ?? "",
                Cantidad: r.quantity ?? r.qty ?? "",
                Precio: r.price != null ? Number(r.price).toFixed(2) : "",
                Total: r.total != null ? Number(r.total).toFixed(2) : "",
                Estatus: r.statusText ?? "",
                Error: r.error ?? ""
            }));

            const ws = XLSX.utils.json_to_sheet(dataForExcel);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "DME_Detail");

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/octet-stream" });

            const pad = (n) => String(n).padStart(2, "0");
            const now = new Date();
            const name = `dme_detail_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}.xlsx`;

            saveAs(blob, name);
        } catch (err) {
            console.error("Export to Excel failed:", err);
            alert("No se pudo exportar a Excel.");
        } finally {
            setExporting(false);
        }
    };

    const filtered = useMemo(() => {
        const nq = norm(q);
        if (!nq) return rows;
        return rows.filter((r) =>
            [
                r.reference,
                r.branch,
                r.sku,
                r.mu ?? r.MU,
                r.quantity ?? r.qty,
                r.price,
                r.total,
                r.statusText,
                r.error,
                r.source,
                r.clientQA,
                r.skuQA,
                r.clientName,
                r.skuDescription,
            ]
                .map(norm)
                .some((v) => v.includes(nq))
        );
    }, [rows, q]);

    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIdx = (page - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, total);
    const pageRows = filtered.slice(startIdx, endIdx);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    if (!data) return null;

    return (
        <div className="bg-white rounded-xl shadow p-4">
            {/* Header con datos del log */}
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 break-all">{data.fileName}</h3>
                    <p className="text-sm text-gray-500">
                        Cadena: <span className="font-medium">{data.chain}</span>
                    </p>
                    <p className="text-sm text-gray-500">
                        Fecha: {data.date ? new Date(data.date).toLocaleString().replace(",", "") : "-"}
                    </p>
                    {/*data.results && <p className="text-sm text-gray-600 mt-1">{data.results}</p>*/}
                </div>

                {/* b�squeda + status + volver */}
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar en detalle (SKU, ref, error, etc.)"
                        className="border rounded px-3 py-2 text-sm w-72"
                    />

                    <button
                        onClick={exportToExcel}
                        disabled={exporting}
                        className="bg-green-800 text-white px-3 py-2 rounded text-sm hover:bg-green-900 disabled:opacity-60"
                    >
                        {exporting ? "Exportando..." : "Exportar a Excel"}
                    </button>

                    {!hideCloseButton && (
                        <button
                            onClick={onBack}
                            className="px-3 py-1 bg-gray-700 text-white rounded"
                        >
                            Cerrar
                        </button>
                    )}
                </div>
            </div>

            {/* tabla principal */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left border-b bg-gray-50">
                            <th className="py-2 px-3">Referencia</th>
                            <th className="py-2 px-3">Sucursal</th>
                            <th className="py-2 px-3">Cliente interno</th>
                            <th className="py-2 px-3">Nombre</th>
                            <th className="py-2 px-3">SKU</th>
                            <th className="py-2 px-3">SKU interno</th>
                            <th className="py-2 px-3">Descripci&oacute;n</th>
                            <th className="py-2 px-3">MU</th>
                            <th className="py-2 px-3">Cantidad</th>
                            <th className="py-2 px-3">Precio</th>
                            <th className="py-2 px-3">Total</th>
                            <th className="py-2 px-3">Estatus</th>
                            <th className="py-2 px-3">Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((r, idx) => (
                            <tr key={idx} className="border-b">
                                <td className="py-2 px-3">{r.reference || "-"}</td>
                                <td className="py-2 px-3">{r.branch || "-"}</td>
                                <td className="py-2 px-3">{r.clientQA ?? "-"}</td>
                                <td className="py-2 px-3">{r.clientName ?? "-"}</td>
                                <td className="py-2 px-3">{r.sku || "-"}</td>
                                <td className="py-2 px-3">{r.skuQA ?? "-"}</td>
                                <td className="py-2 px-3">{r.skuDescription ?? "-"}</td>
                                <td className="py-2 px-3">{r.mu ?? r.MU ?? "-"}</td>
                                <td className="py-2 px-3">{r.quantity ?? r.qty ?? "-"}</td>
                                <td className="py-2 px-3">
                                    {r.price != null ? Number(r.price).toFixed(2) : "-"}
                                </td>
                                <td className="py-2 px-3">
                                    {r.total != null ? Number(r.total).toFixed(2) : "-"}
                                </td>
                                <td className="py-2 px-3">
                                    <span
                                        className={`px-2 whitespace-nowrap py-1 rounded text-xs ${r.statusText === "Por Clasificar"
                                                ? "bg-green-100 text-green-700"
                                                : r.statusText === "Pendiente"
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : r.statusText === "Retenido"
                                                    ? "bg-purple-100 text-purple-700"
                                                    : r.statusText === "Duplicado"
                                                        ? "bg-blue-100 text-blue-700"
                                                        : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {r.statusText}
                                    </span>
                                </td>
                                <td className="py-2 px-3">{r.error || "-"}</td>
                            </tr>
                        ))}
                        {pageRows.length === 0 && (
                            <tr>
                                <td colSpan="10" className="py-6 text-center text-gray-500">
                                    Sin coincidencias en esta p&aacute;gina.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer con paginaci�n */}
            {totalPages > 1 && (
                <div className="flex flex-wrap justify-between items-center gap-3 mt-4 border-t pt-3">
                    <div className="text-sm text-gray-600">
                        Mostrando{" "}
                        <span className="font-medium">{total ? startIdx + 1 : 0}</span> - 
                        <span className="font-medium">{endIdx}</span> de{" "}
                        <span className="font-medium">{total}</span> registros
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm text-gray-600">Filas</label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="border rounded px-2 py-1 text-sm"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            
                        </select>

                        <button
                            disabled={page <= 1}
                            onClick={goPrev}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            &lt;
                        </button>

                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">P&aacute;gina</span>
                            <select
                                value={page}
                                onChange={(e) => setPage(Number(e.target.value))}
                                className="border rounded px-2 py-1 text-sm"
                            >
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                            <span className="text-sm text-gray-600">de {totalPages}</span>
                        </div>

                        <button
                            disabled={page >= totalPages}
                            onClick={goNext}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DmeLogDetail;